import _ from 'lodash';
import { useEffect } from 'react';
import { FormikProvider, useField, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import moment from 'moment';

import { Snippet } from '@treams/treams-ui';
import { useHistory } from '@treams/treams-routes';

import * as graphql from 'generated/graphql';
import Field, { FieldType } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import Label from 'packages/kakapo-ui-kit/components/forms/controls/Label';
import Modal, {
	ModalBackground,
	ModalContent,
} from 'packages/kakapo-ui-kit/components/modals/Modal';
import ModalHeader, {
	ModalHeaderTitle,
} from 'packages/kakapo-ui-kit/components/modals/ModalHeader';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

function TalentReviewTemplateSelector() {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const [{ value }, , { setValue }] = useField('templateID');

	const query = graphql.useTalentReviewTemplatesQuery({
		variables: { isArchived: false, first: 1000 },
	});

	const options = _.sortBy(
		query.data?.talentReviewTemplates.edges.map(({ node }) => ({
			value: node.id,
			label: localize(node.title).text,
		})) ?? [],
		'label',
	);

	useEffect(() => {
		if (options.length === 1 && !value) {
			setValue(options[0].value);
		}
	}, [options.length]);

	if (!query.data) {
		return (
			<Box mb={4}>
				<Label title={t('talentReviews.create.templateLabel')} />
				<Spinner isLoading size="18px" />
			</Box>
		);
	}

	if (options.length === 1) {
		return null;
	}

	const description = query.data?.talentReviewTemplates.edges.find(({ node }) => node.id === value)
		?.node.description;

	return (
		<Box mb={4}>
			<Label title={t('talentReviews.create.templateLabel')} />
			<Select
				options={options}
				defaultValue={options.find((option) => option.value === value)}
				onChange={(option) => setValue(option!.value)}
			/>
			{description && (
				<Snippet maxHeight={100}>
					<QuillText quill={localize(description).text} />
				</Snippet>
			)}
		</Box>
	);
}

export default function CreateTalentReviewModal(props: {
	type: 'team' | 'department';
	groupID: string;
	returnUrl?: string;
}) {
	const { t } = useTranslation();

	const history = useHistory();

	const placeholder = t('talentReviews.create.namePlaceholder', { date: moment().format('L') });

	const [create, { loading }] = graphql.useCreateTalentReviewMutation();

	const formik = useFormik({
		initialValues: {
			title: '',
			templateID: undefined as undefined | string,
		},
		onSubmit: async (values) => {
			const response = await create({
				variables: {
					input: {
						groupID: props.groupID,
						title: values.title || placeholder,
						templateID: values.templateID!,
					},
				},
			});

			history.push(`/talent-reviews/${response.data!.createTalentReview.talentReview.id}`);
		},
	});

	return (
		<ModalBackground>
			<Modal>
				<ModalHeader
					returnUrl={props.returnUrl ?? `/${props.type}/${props.groupID}/talent-reviews`}
				>
					<ModalHeaderTitle>{t('talentReviews.create.title')}</ModalHeaderTitle>
				</ModalHeader>
				<ModalContent>
					<FormikProvider value={formik}>
						<Box mb={4}>
							<Label title={t('talentReviews.create.nameLabel')} />
							<Field
								name="title"
								type={FieldType.Text}
								unspaced
								fullWidth
								placeholder={placeholder}
							/>
						</Box>

						<TalentReviewTemplateSelector />

						<Flex justifyContent="flex-end">
							<Box mt={3}>
								<Button
									size={ButtonSize.Small}
									onClick={() => formik.submitForm()}
									enabled={!loading && !!formik.values.templateID}
								>
									{t('talentReviews.create.startButton.title')}
								</Button>
							</Box>
						</Flex>
					</FormikProvider>
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}
